import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ListTableReload } from '../../type';

/**
 * The shortest a reload skeleton stays up. A cached endpoint can answer in a few
 * dozen milliseconds, and a skeleton that flashes for one frame reads as a glitch
 * rather than as "the list was refetched".
 */
const MIN_RELOAD_MS = 400;

/**
 * Whether the `ListPageCard` around a table is reloading. The card draws the reload
 * button but the table draws the rows, so this is how the one tells the other —
 * without every page threading an `isReloading` prop from one to the other.
 */
export const ListTableReloadContext = createContext(false);

export const useListTableReloadContext = (): boolean => useContext(ListTableReloadContext);

/**
 * Wraps a reload callback so the caller knows when it is in flight: `isReloading`
 * is true from the click until the returned promise settles (and for at least
 * `MIN_RELOAD_MS`). A callback that returns nothing leaves it false — there is
 * nothing to wait on.
 */
export const useTableReload = (
  onReload: ListTableReload | undefined
): { isReloading: boolean; reload: () => void } => {
  const [isReloading, setIsReloading] = useState(false);
  /** Ignores a settle from an earlier click once a newer one is in flight. */
  const runRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reload = useCallback(() => {
    const result = onReload?.();
    if (!result || typeof result.then !== 'function') return;

    const run = ++runRef.current;
    setIsReloading(true);
    const minimum = new Promise((resolve) => setTimeout(resolve, MIN_RELOAD_MS));
    void Promise.allSettled([result, minimum]).then(() => {
      if (mountedRef.current && runRef.current === run) setIsReloading(false);
    });
  }, [onReload]);

  return { isReloading, reload };
};
